import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  DecimalScale,
  FormDialogMode,
  UserPermission
} from '@globaleris/ui.library'
import { Add, Delete, Edit, LibraryAdd } from '@material-ui/icons'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { generateUniqueKeyWithNoPrefix } from '../../../helpers'
import { FormDialogDefinitionProps, useFormDialog, useHttpError } from '../../../hooks'
import { DataTableErrorHandler } from '../../errors/scopes/dataTable/dataTableError'
import { mapDataTableRowIcons } from './quantityDataTable.mapper'
import { QuantityDataTableRow } from './quantityDataTable.model'

export interface QuantityDataTableProps {
  tableData: QuantityDataTableRow[] | null | undefined
  canEdit?: boolean
  onAddMassiveClickHandler?: () => void
  onChangeDataTable?: (data: QuantityDataTableRow[]) => void
  dataTableUserPermissions?: UserPermission[]
}

export const QuantityDataTable: React.FC<QuantityDataTableProps> = ({
  canEdit = true,
  tableData,
  onAddMassiveClickHandler,
  onChangeDataTable,
  dataTableUserPermissions
}) => {
  const [t] = useTranslation(['quantityDataTable', 'globalerisLibrary', 'enums'])
  const { showError, clearError, errorProps } = useHttpError()
  const { openForm, updateFormDefinition, closeForm, formProps, CustomFormDialog, setFormWaiting } =
    useFormDialog()

  const data = tableData ? mapDataTableRowIcons(tableData) : []

  // DataTable Config Starts
  const columns: DataTableColumn[] = [
    {
      name: 'tempId',
      label: '',
      display: false
    },
    {
      name: 'actions',
      label: '',
      hideForResponsive: true,
      type: DataType.HTML,
      columnWidth: 65
    },
    {
      name: 'detailID',
      display: false,
      type: DataType.NUMBER
    },
    {
      name: 'id',
      display: false,
      type: DataType.NUMBER
    },
    {
      name: 'startDate',
      label: t('quantityDataTable:dataTable.columns.startDate'),
      type: DataType.DATE
    },
    {
      name: 'endDate',
      label: t('quantityDataTable:dataTable.columns.endDate'),
      type: DataType.DATE
    },
    {
      name: 'quantity',
      label: t('quantityDataTable:dataTable.columns.quantity'),
      type: DataType.NUMBER,
      decimalScale: DecimalScale.CURRENCY
    }
  ]

  const insertHandler = async () => {
    onAddValuesActionHandler()
    return true
  }

  const options: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: false,
    elevation: 0,
    download: false,
    print: false,
    selectableRows: false,
    rowsPerPage: 20,
    rowsPerPageOptions: [20, 50, 100],
    canInsert: canEdit,
    canEdit: false,
    canDelete: false,
    customInsertEditModal: canEdit,
    onRowInsert: canEdit ? insertHandler : undefined,
    fabCustomActions: [
      {
        userPermission: UserPermission.EDIT,
        icon: <Add fontSize="small" />,
        tooltip: t('quantityDataTable:dataTable.fabCustomActions.add.tooltip'),
        onClick: insertHandler
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <LibraryAdd fontSize="small" />,
        tooltip: t('quantityDataTable:dataTable.fabCustomActions.addMassive.tooltip'),
        onClick: () => {
          onAddMassiveClickHandler && onAddMassiveClickHandler()
        }
      }
    ],
    rowCustomActions: canEdit
      ? [
          {
            userPermission: UserPermission.EDIT,
            icon: <Edit fontSize="small" />,
            tooltip: t('quantityDataTable:dataTable.customActions.editValue.tooltip'),
            onClick: (row: QuantityDataTableRow) => {
              onEditValuesActionHandler(row)
            }
          },
          {
            userPermission: UserPermission.EDIT,
            icon: <Delete fontSize="small" />,
            tooltip: t('quantityDataTable:dataTable.customActions.deletePeriod.tooltip'),
            onClick: (row: QuantityDataTableRow) => {
              const itemHasId = row.id || row.detailID || row.tempId
              if (itemHasId) {
                onDeleteActionHandler(itemHasId)
              }
            }
          }
        ]
      : undefined
  }

  const onAddValuesActionHandler = async () => {
    openForm({
      definition: editValuesDefinition,
      title: t('quantityDataTable:form.addValues.title'),
      okBtnLabel: t('quantityDataTable:form.addValues.actions.okButton'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      onClose: formData => {
        formData ? addRowValue(formData) : closeForm()
      }
    })
  }

  const onEditValuesActionHandler = async (rowData: QuantityDataTableRow) => {
    openForm({
      definition: editValuesDefinition,
      title: t('quantityDataTable:form.editValues.title'),
      okBtnLabel: t('quantityDataTable:form.editValues.actions.okButton'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      inputData: rowData,
      onClose: formData => {
        formData ? editRowValue(formData) : closeForm()
      }
    })
  }

  const addRowValue = async (quantityRow: QuantityDataTableRow) => {
    const newItem = {
      ...quantityRow,
      tempId: generateUniqueKeyWithNoPrefix()
    }

    const newTableData = [...data, newItem]
    onChangeDataTable && onChangeDataTable(newTableData)
    closeForm()
  }

  const editRowValue = async (quantityRow: QuantityDataTableRow) => {
    const foundItem = data.find(
      c =>
        (c.idDetail && c.idDetail === quantityRow.idDetail) ||
        (c.detailID && c.detailID === quantityRow.detailID) ||
        (c.id && c.id === quantityRow.id) ||
        (!c.idDetail && !c.detailID && !c.id && c.tempId === quantityRow.tempId)
    )

    const updatedRow = {
      ...foundItem,
      ...quantityRow,
      tempId: `${foundItem?.tempId ?? foundItem?.idDetail ?? foundItem?.detailID ?? foundItem?.id}`
    }

    const newTableData = data.map(d => {
      if (
        (d.idDetail && d.idDetail === updatedRow.idDetail) ||
        (d.detailID && d.detailID === updatedRow.detailID) ||
        (d.id && d.id === updatedRow.id) ||
        // If the row has no idDetail, detailID or id, we compare by tempId
        (!d.idDetail && !d.detailID && !d.id && d.tempId === updatedRow.tempId)
      ) {
        return updatedRow
      }
      return d
    })
    onChangeDataTable && onChangeDataTable(newTableData)

    closeForm()
  }

  const onDeleteActionHandler = async (id: number | string) => {
    const newTableData = data.filter(
      item =>
        (item.idDetail && item.idDetail !== id) ||
        (item.detailID && item.detailID !== id) ||
        (item.id && item.id !== id) ||
        // If the row has no idDetail, detailID or id, we compare by tempId
        (!item.idDetail && !item.detailID && !item.id && item.tempId !== id)
    )

    onChangeDataTable && onChangeDataTable(newTableData)
  }

  const editValuesDefinition: FormDialogDefinitionProps =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT_PRICE_VALUES',
        sections: [
          {
            inputs: [
              {
                name: 'startDate',
                label: 'Fecha de inicio',
                type: DataType.DATE,
                required: true
              },
              {
                name: 'endDate',
                label: 'Fecha de fin',
                type: DataType.DATE,
                required: true
              },
              {
                name: 'quantity',
                label: 'Cantidad',
                type: DataType.NUMBER,
                required: true,
                decimalScale: DecimalScale.CURRENCY
              }
            ]
          }
        ]
      }
    }, [])

  useEffect(() => {
    updateFormDefinition(editValuesDefinition)
  }, [editValuesDefinition])

  // DataTable Config Ends

  return data != null ? (
    <>
      <DataTable
        data={data}
        columns={columns}
        options={options}
        info={DataTableErrorHandler(errorProps)}
        userPermissions={dataTableUserPermissions}
      />
      <CustomFormDialog {...formProps} />
    </>
  ) : null
}
